import Text, { TextFontWeight, TextType } from "@src/lib/ui/Text";
import { CSSProperties, FC, MouseEvent } from "react";
import { isUndefined, truncate } from "lodash";
import { cn } from "@src/lib/utils/cn";
import XIcon from "@src/lib/icons/18px/x";

type Props = {
  title: string;
  subtitle?: string;
  onRemove?: (e: MouseEvent) => void;
  onClick?: () => void;
  style?: CSSProperties;
  className?: string;
  id?: number | string;
  color?: string;
  dataTestId?: string;
  textType?: TextType;
  textFontWeight?: TextFontWeight;
  truncateAt?: number;
};

type ToneName =
  | "blue"
  | "purple"
  | "green"
  | "red"
  | "orange"
  | "yellow"
  | "gray";

const PILL_COLORS: ToneName[] = ["blue", "purple", "green", "red", "orange"];

type ColorValues = {
  bg: string;
  title: string;
  subtitle: string;
  icon: string;
};
const toneNameToValues: { [key in ToneName]: ColorValues } = {
  blue: {
    bg: "bg-blue-1",
    title: "text-blue-10",
    subtitle: "text-blue-7",
    icon: "text-blue-10",
  },
  purple: {
    bg: "bg-purple-1",
    title: "text-purple-10",
    subtitle: "text-purple-7",
    icon: "text-purple-10",
  },
  green: {
    bg: "bg-green-1",
    title: "text-green-10",
    subtitle: "text-green-7",
    icon: "text-green-10",
  },
  red: {
    bg: "bg-red-1",
    title: "text-red-10",
    subtitle: "text-red-7",
    icon: "text-red-10",
  },
  orange: {
    bg: "bg-orange-1",
    title: "text-orange-10",
    subtitle: "text-orange-7",
    icon: "text-orange-10",
  },
  yellow: {
    bg: "bg-yellow-1",
    title: "text-yellow-10",
    subtitle: "text-yellow-7",
    icon: "text-yellow-10",
  },
  gray: {
    bg: "bg-gray-1",
    title: "text-gray-10",
    subtitle: "text-gray-7",
    icon: "text-gray-10",
  },
};

const Pill: FC<Props> = ({
  title,
  subtitle,
  onClick,
  onRemove,
  color = "gray",
  id,
  style,
  className,
  dataTestId,
  textType = "P3",
  textFontWeight = "Regular",
  truncateAt,
}) => {
  const baseColor = isUndefined(id) ? color : getBaseColorFromId(id);
  const pillColors = toneNameToValues[baseColor as ToneName];
  return (
    <div
      className={cn(
        "flex items-center w-fit px-1.5 rounded",
        pillColors.bg,
        onClick && "cursor-pointer",
        className,
      )}
      style={style}
      onClick={onClick}
      data-testid={dataTestId}
    >
      <Text
        type={textType}
        fontWeight={textFontWeight}
        className={cn("whitespace-nowrap", pillColors.title)}
      >
        {truncateAt ? truncate(title, { length: truncateAt }) : title}
      </Text>
      {subtitle && (
        <div className="ml-2">
          <Text
            variant="fine-print-description"
            className={pillColors.subtitle}
          >
            {subtitle}
          </Text>
        </div>
      )}
      {onRemove && (
        <XIcon
          className={cn(
            "-mr-1 ml-1 flex size-[14px] cursor-pointer items-center justify-center",
            pillColors.icon,
          )}
          onClick={onRemove}
        />
      )}
    </div>
  );
};

export default Pill;

export const hashStringToNumber = (str: string): number => {
  if (!str) {
    return 1;
  }
  return Math.abs(
    str
      .split("")
      .map((v) => v.charCodeAt(0))
      .reduce((a, v) => (a + ((a << 7) + (a << 3))) ^ v),
  );
};

const idHash = (id: number | string): number => {
  if (typeof id === "number") {
    return id;
  } else {
    return hashStringToNumber(id);
  }
};

const getBaseColorFromId = (id: number | string): ToneName => {
  return PILL_COLORS[idHash(id) % PILL_COLORS.length];
};

export const getPillColorsFromId = (id: number | string): ColorValues => {
  const baseColor = getBaseColorFromId(id);
  return toneNameToValues[baseColor];
};
